import { useInfiniteQuery, useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { toast } from "sonner";
import { supabase } from "@/lib/supabase";
import { invalidateFor } from "@/lib/cache-registry";
import { logEvent } from "@/hooks/use-event-log";
import { useAuth } from "@/contexts/AuthContext";
import { useImpersonation } from "@/contexts/ImpersonationContext";
import type { DBCustomerActivity } from "@/lib/db-types";

export interface CustomerActivityRow extends DBCustomerActivity {
  contact: { id: string; full_name: string } | null;
  user: { id: string; full_name: string } | null;
}

/** Keyset-markør: neste side starter strengt etter (occurred_at, id) i synkende rekkefølge. */
export interface ActivityCursor {
  occurred_at: string;
  id: string;
}

const SELECT_WITH_RELATIONS =
  "*, contact:customer_contacts(id, full_name), user:users!customer_activities_user_id_fkey(id, full_name)";

async function fetchActivityPage(
  customerId: string,
  limit: number,
  before?: ActivityCursor,
  opportunityId?: string,
): Promise<CustomerActivityRow[]> {
  let q = supabase
    .from("customer_activities")
    .select(SELECT_WITH_RELATIONS)
    .eq("customer_id", customerId)
    .order("occurred_at", { ascending: false })
    .order("id", { ascending: false })
    .limit(limit);
  if (opportunityId) q = q.eq("opportunity_id", opportunityId);
  if (before) {
    q = q.or(
      `occurred_at.lt.${before.occurred_at},and(occurred_at.eq.${before.occurred_at},id.lt.${before.id})`,
    );
  }
  const { data, error } = await q;
  if (error) throw error;
  return (data ?? []) as unknown as CustomerActivityRow[];
}

/**
 * Kontaktlogg for én kunde, nyeste først. Paginerer med keyset
 * (occurred_at, id) og `limit` i stedet for fetchAllPaginated: kundekortet
 * trenger bare de siste radene, og listen er bundet av customer_id.
 */
export function useCustomerActivities(
  customerId: string | undefined,
  opts: { limit?: number; before?: ActivityCursor } = {},
) {
  const { limit = 50, before } = opts;
  return useQuery({
    queryKey: ["customer-activities", customerId, { limit, before }],
    queryFn: () => fetchActivityPage(customerId!, limit, before),
    enabled: !!customerId,
    staleTime: 60 * 1000,
  });
}

/**
 * Samme keyset-paginering som useCustomerActivities, men som uendelig liste
 * («Vis mer» på kontaktloggen). Nøkkelen deler prefiks med useCustomerActivities,
 * så invalidateFor("customer_activities", { customerId }) treffer begge.
 */
export function useCustomerActivitiesInfinite(customerId: string | undefined, pageSize = 50, opportunityId?: string) {
  return useInfiniteQuery({
    queryKey: ["customer-activities", customerId, "infinite", pageSize, opportunityId ?? null],
    queryFn: ({ pageParam }) => fetchActivityPage(customerId!, pageSize, pageParam, opportunityId),
    initialPageParam: undefined as ActivityCursor | undefined,
    getNextPageParam: (last): ActivityCursor | undefined => {
      if (last.length < pageSize) return undefined;
      const tail = last[last.length - 1];
      return { occurred_at: tail.occurred_at, id: tail.id };
    },
    enabled: !!customerId,
    staleTime: 60 * 1000,
  });
}

export interface LastContact {
  customer_id: string;
  last_contact_at: string;
  last_contact_type: DBCustomerActivity["type"];
  last_contact_user_id: string | null;
}

/** Siste faktiske kontakt (e-post, samtale, SMS, møte), fra viewet customer_last_contact_v. */
export function useLastContact(customerId: string | undefined) {
  return useQuery({
    queryKey: ["customer-last-contact", customerId],
    queryFn: async (): Promise<LastContact | null> => {
      const { data, error } = await supabase
        .from("customer_last_contact_v")
        .select("*")
        .eq("customer_id", customerId!)
        .maybeSingle();
      if (error) throw error;
      return (data as LastContact | null) ?? null;
    },
    enabled: !!customerId,
    staleTime: 60 * 1000,
  });
}

export interface LogActivityInput {
  customer_id: string;
  type: Extract<DBCustomerActivity["type"], "call" | "note" | "meeting">;
  contact_id?: string | null;
  /** Kun til hendelsesloggen ("Samtale med Kari"); lagres ikke på raden. */
  contact_name?: string | null;
  subject?: string | null;
  body?: string | null;
  occurred_at?: string;
}

/**
 * Manuell logging av samtale, notat eller møte. email_* og sms_out skrives
 * bare av edge functions. `user_id` er den som utførte (activeUser, også ved
 * impersonering); `created_by` og audit-aktør er den faktisk innloggede.
 */
export function useLogCustomerActivity() {
  const qc = useQueryClient();
  const { user: authUser } = useAuth();
  const { activeUser, isImpersonating } = useImpersonation();
  return useMutation({
    mutationFn: async (input: LogActivityInput): Promise<DBCustomerActivity> => {
      const { data, error } = await supabase
        .from("customer_activities")
        .insert({
          customer_id: input.customer_id,
          type: input.type,
          contact_id: input.contact_id ?? null,
          subject: input.subject?.trim() || null,
          body: input.body?.trim() || null,
          occurred_at: input.occurred_at ?? new Date().toISOString(),
          status: "logged",
          user_id: activeUser?.id ?? authUser?.id ?? null,
          created_by: authUser?.id ?? null,
        })
        .select()
        .single();
      if (error) throw error;
      return data as DBCustomerActivity;
    },
    onSuccess: async (row, input) => {
      if (authUser?.id) {
        logEvent({
          event_type: "crm_activity_logged",
          entity_type: "customer_activities",
          entity_id: row.id,
          user_id: authUser.id,
          payload: {
            customer_id: row.customer_id,
            type: row.type,
            contact_name: input.contact_name ?? null,
            ...(isImpersonating && activeUser ? { on_behalf_of: activeUser.id } : {}),
          },
        });
      }
      await invalidateFor(qc, "customer_activities", { customerId: row.customer_id });
    },
    onError: (err: Error) => toast.error(`Kunne ikke logge kontakten: ${err.message}`),
  });
}
